<template>
  <div class="practice-pulse-page">    
    <b-row>
      <b-col md="12">
        <h2 class="page-title mb-4 text-center">Practice Ops
          <i id="pp-settings" class="la la-cog px-2 pull-right" style="font-size: 20px" @click="configurePracticePulse"/>
          <i id="pp-help" class="la la-question px-2 pull-right" style="font-size: 20px" @click="showPPHelp" />
          <i id="pp-privacy" style="font-size: 20px" :class="privacyClass" @click="togglePrivacy"/>
          <i id="pp-report" v-if="pp.ops && Object.keys(pp.ops).length > 0" style="font-size: 20px" class="la la-bar-chart-o px-2 pull-right" @click="showDetails('report', {practicePulseSettings})"/>

            <!--
            <i id="pp-refresh" class="la la-refresh px-2 pull-right" style="font-size: 20px" @click="refreshPP"/>
            <i id="pp-delete" class="la la-remove px-2 pull-right" style="font-size: 20px" @click="showModal('reset-pp-confirm')"/>
            -->

          <b-tooltip :target="`pp-settings`" triggers="hover">
            Configure Settings
          </b-tooltip>
          <b-tooltip :target="`pp-privacy`" triggers="hover">
            <span v-if="this.privacyOn">Privacy is ON. Click to turn it Off</span>
            <span v-else>Privacy is off. Click to turn it ON</span>
          </b-tooltip>

          <!--
          <b-tooltip :target="`pp-refresh`" triggers="hover">
            Not seeing certain Ops? Click this button to refresh
          </b-tooltip>
          <b-tooltip :target="`pp-delete`" triggers="hover">
            Click this button to delete everything and start over
          </b-tooltip>
          -->

          <b-tooltip :target="`pp-report`" triggers="hover">
            View Report
          </b-tooltip>
          <b-tooltip :target="`pp-help`" triggers="hover">
            Learn how to set up and use Practice Pulse
          </b-tooltip>
        </h2>
      </b-col>
    </b-row>

    <!-- Not paid for or not configured state -->
    <div v-if="allSettings && (!practicePulsePaidFor || !practicePulseSettings)" class="pp-setup-container">
      <!-- Feature not enabled -->
      <div v-if="!practicePulsePaidFor" class="pp-setup-card feature-locked">
        <div class="setup-icon-wrap">
          <i class="fa fa-lock"></i>
        </div>
        <div class="setup-content">
          <h3 class="setup-title">Premium Feature</h3>
          <p class="setup-description" v-html="featureNotEnabledMsg"></p>
        </div>
      </div>

      <!-- Needs configuration -->
      <div v-else-if="practicePulsePaidFor && !practicePulseSettings" class="pp-setup-card needs-config">
        <div class="setup-icon-wrap">
          <i class="fa fa-cog"></i>
        </div>
        <div class="setup-content">
          <h3 class="setup-title">Configure Practice Pulse</h3>
          <p class="setup-description">You haven't configured your settings yet. Click the gear icon in the top right to get started.</p>
          <button class="setup-btn" @click="configurePracticePulse">
            <i class="fa fa-cog mr-2"></i> Configure Now
          </button>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Skeleton loading state -->
      <PracticePulseSkeleton v-if="!ppLoaded" />

      <!-- Empty state when no ops configured -->
      <div v-if="ppLoaded && (!pp.ops || Object.keys(pp.ops).length === 0)" class="pp-empty-state-container">
        <div class="pp-empty-state">
          <div class="empty-state-visual">
            <div class="visual-circles">
              <div class="circle circle-1"></div>
              <div class="circle circle-2"></div>
              <div class="circle circle-3"></div>
            </div>
            <div class="visual-icon">
              <i class="fa fa-heartbeat"></i>
            </div>
          </div>
          <h3 class="empty-state-title">Ready to Monitor Patient Flow</h3>
          <p class="empty-state-text">Practice Pulse helps you track patients from check-in to checkout in real-time. Configure your operatories and status settings to get started.</p>
          <div class="empty-state-features">
            <div class="feature-item">
              <i class="fa fa-check-circle"></i>
              <span>Track patient arrivals</span>
            </div>
            <div class="feature-item">
              <i class="fa fa-check-circle"></i>
              <span>Monitor operatory status</span>
            </div>
            <div class="feature-item">
              <i class="fa fa-check-circle"></i>
              <span>Real-time alerts</span>
            </div>
          </div>
          <button class="empty-state-btn" @click="configurePracticePulse">
            <i class="fa fa-cog mr-2"></i> Configure Settings
          </button>
        </div>
      </div>

        <b-container fluid class="mt-4" v-if="ppLoaded && pp.ops && Object.keys(pp.ops).length > 0">
          <ErrorDisplay/>
          <b-row style="min-height: 600px;">
            <b-col sm="1" md="1" lg="2" xl="2" style="border-right: 2px dashed #bdbbb7;max-height:800px;overflow-y:scroll;">             
              <widget title="Check-in" class="check-in-header" customHeader>
                  <div class="d-flex justify-content-between mt-1">
                    <span>
                        <span class="fw-semi-bold fs-mini">Arrived </span> <span class="text-light fs-mini badge badge-default badge-pill">{{ howManyArrived }}</span>
                    </span>
                    <span>
                        <span class="fw-semi-bold fs-mini">Ready </span> <span class="text-light fs-mini badge badge-default badge-pill">{{ howManyReady }}</span>
                    </span>
                  </div>
                  <!--
                    ⛔ STILL DISABLED, and the reason is no longer "it doesn't work". 2026-09-21:
                    the arithmetic and lifecycle bugs behind the original note are fixed (threshold
                    compared minutes-within-the-hour; the count came from a Firestore map that could
                    only grow; the master toggle was ignored) — see src/utils/waitElapsed.js and
                    tests/component/waitElapsed.spec.js.

                    What is NOT fixed is the MEASUREMENT. "Minutes since marked ready" only means
                    "waiting" if staff move patients out of check-in once they are seated. Measured
                    on prod 2026-09-21: at the one real site with this feature configured (limit 5
                    minutes), all 12 checked-in patients sat in "Ready to go Back" all day, with
                    waits of 4 to 349 minutes — the 7:28am patient still in check-in at 1:07pm.
                    Nothing was leaking and the nightly reset was fine; they simply use this column
                    as an arrivals list. So this banner would read "11 waiting more than 5 minutes"
                    every day, permanently, and staff would learn to ignore it inside a week.

                    🔴 Do NOT un-comment this until the metric is redefined (measure against
                    appointment time, or cap the window) or the workflow advances patients off
                    check-in automatically. Enabling it as-is ships a light that is always on.
                  <div class="clearfix" v-if="this.practicePulseSettings && this.practicePulseSettings.elapsedTime && howManyWaitingForMoreThanX > 0">
                    <b-alert class="mt-2" variant="danger" show>
                      <span class="fw-semi-bold fs-mini mt-sm text-white">{{ howManyWaitingForMoreThanX }} waiting more than {{ this.practicePulseSettings.elapsedTime }} minutes since ready</span>
                    </b-alert>
                  </div>
                  -->
                </widget>
                
                <draggable
                  id="checkin"          
                  data-source="juju"
                  :list="pp.ops.checkin"
                  class="list-group"
                  tag="div"
                  v-bind="dragOptions"
                  draggable=".item"              
                  @end="onEnd($event, 'checkin')"
                  @start="onStart($event, 'checkin')"
                  @change="onChange($event, 'checkin')"                                          
                >
                    <widget class="list-group-item item" v-for="(item, index) in pp.ops.checkin" :key="`checkin-${item.aptNum}`" :style="getCheckinProvStyle(item)" @close-widget="_deleteFromCheckin(item, index)" customHeader close>
                      
                      <div class="mt-2">
                      
                        <div class="mt-n-xs">                           
                           <span class="thumb mr mt-n-sm">
                             <img class="rounded-circle" src='../../assets/people/user2.png' alt="..." />
                             <i v-if="visualAlerts[item.patNum]" class="status status-bottom bg-danger"></i>
                           </span>                           
                      </div>
                   
                      <div>
                            <h6 class="fs-mini fw-semi-bold" >{{item.name}}<i v-if="isBirthday(item.patNum)" class="pull-right glyphicon glyphicon-gift" /></h6>
                            <b-alert class="mt-2" variant="danger" v-if="visualAlerts[item.patNum] && patientTimers[item.patNum] && patientTimers[item.patNum].elapsed" show>
                              <span class="fw-semi-bold fs-mini mt-sm text-white">Waiting: {{ patientTimers[item.patNum].elapsed }}</span>
                            </b-alert>                            
                      </div>                      
                      <p class="text-light fs-mini  mb-1">{{ item.procDescript }}</p>
                      <p class="text-light fs-mini mt-sm">{{ item.note }}</p>

                        <footer class="bg-body-light">
                          <ul class="post-links">
                            <li>
                              <i :id="`color-checkin-${item.patNum}`" class="fa fa-circle" :style="numToColor(item.checkinColor)"/>
                              <b-tooltip :target="`color-checkin-${item.patNum}`" triggers="hover">
                                {{ item.checkinStatus }}
                              </b-tooltip>
                            </li>
                            <li v-if="practicePulseSettings.patientBalanceAlert === 'TRUE'">
                              <i :id="`balance-checkin-${index}`" class="fa fa-dollar" :style="getPatientBalanceStyle(item)" @click="showDetails('balance', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`balance-checkin-${index}`" triggers="hover">
                                Balance
                              </b-tooltip>
                            </li>
                            <li v-if="practicePulseSettings.patientFamilyOverdueRecallAlert === 'TRUE'">
                              <i :id="`recall-checkin-${index}`" class="fa fa-retweet" :style="getRecallStyle(item)" @click="showDetails('recall', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`recall-checkin-${index}`" triggers="hover">
                                Recall
                              </b-tooltip> 
                            </li>
                            <li v-if="practicePulseSettings.showOrthoAlert === 'TRUE'">
                              <i :id="`ortho-checkin-${index}`" class="fa fa-magnet" :style="getOrthoStyle(item)" @click="showDetails('ortho', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`ortho-checkin-${index}`" triggers="hover">
                                Ortho
                              </b-tooltip>
                            </li>
                            <li v-if="practicePulseSettings.showTasks === 'TRUE'">
                              <i :id="`tasks-checkin-${index}`" class="fa fa-tasks" :style="getTasksStyle(item)" @click="showDetails('tasks', {siteID: siteID, patNum: item.patNum, user: user, tasks: taggedTasks[item.patNum], allSettings: allSettings})"/>
                              <b-tooltip :target="`tasks-checkin-${index}`" triggers="hover">
                                Tasks
                              </b-tooltip>
                            </li>
                            
                            
                            <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none" no-caret dropup boundary="viewport">
                              <template #button-content>
                                <i :id="`checkin-move-${index}`" class="fa fa-share"/>
                                <b-tooltip :target="`checkin-move-${index}`" triggers="hover">
                                  Move to Op
                                </b-tooltip>
                                <span class="sr-only">Move</span>
                              </template>
                              <b-dropdown-item v-for="(v, k) in emptyOps" :key="`op-${k}`" @click="handleMoveFromCheckin(item, k, item.aptNum, item.patNum)">
                                {{ getOpNameFromOpNum(k) }}
                              </b-dropdown-item>
                            </b-dropdown>
                          </ul>                                           
                        </footer>                        
                    </div>         
                    </widget>                
                </draggable>
            </b-col>
            <b-col sm="1" md="1" lg="6" xl="6" style="border-right: 2px dashed #bdbbb7;">
              <b-row v-if="practicePulseSettings && practicePulseSettings.allOps">
                <b-col sm="4" md="4" lg="4" xl="4" v-for="op in practicePulseSettings.allOps" :key="`op-${op.OperatoryNum}`" style="background-color:#ffffff;border-right: 10px solid #f9fbfd;" class="mb-4 operatory-cols">                    
                      <h6 class="mt-2 mb-2 fw-bold">
                        {{ getOpNameFromOpNum(op.OperatoryNum) }}
                        <i class="pull-right fa fa-user" @click="ping(op.OperatoryNum)"/>                        
                      </h6>
                   
                    <draggable
                      id="operatories"
                      data-source="juju"
                      :list="pp.ops[op.OperatoryNum]"
                      class="list-group"
                      tag="div"
                      v-bind="dragOptions"
                      draggable=".item"              
                      @end="onEnd($event, `${op.OperatoryNum}`)"
                      @start="onStart($event, `${op.OperatoryNum}`)"
                      @change="onChange($event, `${op.OperatoryNum}`)"                          
                    >
                        <Widget :id="`${item.patNum}`" class="list-group-item item" v-for="(item, index) in pp.ops[op.OperatoryNum]" :key="`op-${op.OperatoryNum}-${item.patNum}-${item.aptNum}`" :style="getCheckinProvStyle(item)">
                        <div class="mt-2">                          
                          <div class="mt-n-xs">                           
                            <span class="thumb mr mt-n-sm">
                              <img class="rounded-circle" src='../../assets/people/user2.png' alt="..." />                              
                            </span>                            
                          </div>
                          <div>
                            <h6 class="fs-mini mb-xs mt-xs fw-semi-bold" >{{item.name}}<i v-if="isBirthday(item.patNum)" class="pull-right glyphicon glyphicon-gift" /></h6>
                          </div>
                          <p class="text-light fs-mini mt-2 mb-1">{{ item.procDescript }} </p>
                          <p class="text-light fs-mini mt-sm">{{ item.note }}</p>
                              <footer class="bg-body-light">
                                <ul class="post-links">
                                  <li v-if="practicePulseSettings.patientBalanceAlert === 'TRUE'">
                                    <i :id="`balance-${op.OperatoryNum}-${index}`" class="fa fa-dollar" :style="getPatientBalanceStyle(item)" @click="showDetails('balance', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                                    <b-tooltip :target="`balance-${op.OperatoryNum}-${index}`" triggers="hover">
                                      Balance
                                    </b-tooltip>
                                  </li>
                                  <li v-if="practicePulseSettings.patientFamilyOverdueRecallAlert === 'TRUE'">
                                    <i :id="`recall-${op.OperatoryNum}-${index}`" class="fa fa-retweet" :style="getRecallStyle(item)" @click="showDetails('recall', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                                    <b-tooltip :target="`recall-${op.OperatoryNum}-${index}`" triggers="hover">
                                      Recall
                                    </b-tooltip>
                                  </li>
                                  <li v-if="practicePulseSettings.showOrthoAlert === 'TRUE'">
                                    <i :id="`ortho-${op.OperatoryNum}-${index}`" class="fa fa-magnet" :style="getOrthoStyle(item)" @click="showDetails('ortho', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                                    <b-tooltip :target="`ortho-${op.OperatoryNum}-${index}`" triggers="hover">
                                      Ortho
                                    </b-tooltip>
                                  </li>
                                  <li v-if="practicePulseSettings.showTasks === 'TRUE'">
                                    <i :id="`tasks-${op.OperatoryNum}-${index}`" class="fa fa-tasks" :style="getTasksStyle(item)" @click="showDetails('tasks', {siteID: siteID, patNum: item.patNum, user: user, tasks: taggedTasks[item.patNum], allSettings: allSettings})"/>
                                    <b-tooltip :target="`tasks-${op.OperatoryNum}-${index}`" triggers="hover">
                                      Tasks
                                    </b-tooltip>
                                  </li>
                                  <li>
                                    <i :id="`checkout-${op.OperatoryNum}-${index}`" class="fa fa-sign-out" @click="checkoutNotes(op.OperatoryNum, item, index, false)"/>                            
                                    <b-tooltip :target="`checkout-${op.OperatoryNum}-${index}`" triggers="hover">
                                      Check-out
                                    </b-tooltip> 
                                  </li>                                  
                                </ul>
                              </footer>
                        </div>                       
                      </Widget>                       
                    </draggable>                  
                </b-col>
              </b-row>   
            </b-col>
            <b-col sm="1" md="1" lg="2" xl="2" style="max-height:800px;overflow-y:scroll;">              
              <widget title="Check-out" class="check-in-header" customHeader>
                  <div class="d-flex justify-content-between mt-1">
                    <span>
                        <span class="fw-semi-bold fs-mini">Check-out </span> <span class="text-light fs-mini badge badge-default badge-pill">{{ howManyCheckedOut }}</span>
                    </span>
                    <span>
                        <span class="fw-semi-bold fs-mini">Dismissed </span> <span class="text-light fs-mini badge badge-default badge-pill">{{ howManyDismissed }}</span>
                    </span>
                  </div>                  
                </widget>                
                <draggable
                  id="checkout"          
                  data-source="juju"
                  :list="pp.ops.checkout"
                  class="list-group"
                  tag="div"
                  v-bind="dragOptions"
                  draggable=".item"              
                  @end="onEnd($event, 'checkout')"
                  @start="onStart($event, 'checkout')"
                  @change="onChange($event, 'checkout')"                          
                >
                    <widget :class="`list-group-item item ${item.dateTimeDismissed ? 'dismissed' : ''}`" v-for="(item, index) in pp.ops.checkout" :key="`checkout-${item.aptNum}`" :style="getCheckinProvStyle(item)">
                      <div class="mt-2">
                        <div class="mt-n-xs">                           
                           <span class="thumb mr mt-n-sm">
                             <img class="rounded-circle" src='../../assets/people/user2.png' alt="..." />
                           </span>
                        </div>
                        <div>
                            <h6 class="fs-mini mb-xs mt-xs fw-semi-bold" >{{item.name}}<i v-if="isBirthday(item.patNum)" class="pull-right glyphicon glyphicon-gift" /></h6>
                        </div>
                        <p class="text-light fs-mini mt-2 mb-1">{{ item.procDescript }}</p>
                        <p class="text-light fs-mini mt-sm">{{ item.note }}</p>
                        <footer class="bg-body-light">                          
                          <ul class="post-links">
                            <li v-if="practicePulseSettings.patientBalanceAlert === 'TRUE'">
                              <i :id="`checkout-balance-${item.patNum}`" class="pull-right fa fa-dollar" :style="getPatientBalanceStyle(item)" @click="showDetails('balance', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`checkout-balance-${item.patNum}`" triggers="hover">
                                Balance
                              </b-tooltip>
                            </li>                            
                            <li v-if="practicePulseSettings.patientFamilyOverdueRecallAlert === 'TRUE'">
                              <i :id="`checkout-recall-${item.patNum}`" class="pull-right fa fa-retweet" :style="getRecallStyle(item)" @click="showDetails('recall', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`checkout-recall-${item.patNum}`" triggers="hover">
                                Recall
                              </b-tooltip>
                            </li>
                            <li v-if="practicePulseSettings.showOrthoAlert === 'TRUE'">
                              <i :id="`ortho-${item.patNum}-${index}`" class="fa fa-magnet" :style="getOrthoStyle(item)" @click="showDetails('ortho', {siteID: siteID, patientData: item, allSettings: allSettings})"/>
                              <b-tooltip :target="`ortho-${item.patNum}-${index}`" triggers="hover">
                                Ortho
                              </b-tooltip>
                            </li>
                            <li v-if="practicePulseSettings.showTasks === 'TRUE'">
                              <i :id="`checkout-tasks-${item.patNum}`" class="pull-right fa fa-tasks" :style="getTasksStyle(item)" @click="showDetails('tasks', {siteID: siteID, patNum: item.patNum, user: user, tasks: taggedTasks[item.patNum], allSettings: allSettings})"/>
                              <b-tooltip :target="`checkout-tasks-${item.patNum}`" triggers="hover">
                                Tasks
                              </b-tooltip>
                            </li>
                            <li>
                              <i :id="`checkout-checkout-${item.patNum}`" class="fa fa-sign-out" @click="checkoutNotes('checkout', item, index, false)"/>                            
                              <b-tooltip :target="`checkout-checkout-${item.patNum}`" triggers="hover">
                                Check-out
                              </b-tooltip> 
                            </li>
                            <li>
                              <i :id="`checkout-dismiss-${item.patNum}`" class="fa fa-times-circle" @click="dismiss(item, index)"/>                              
                              <i v-if="dismissInProgress[item.patNum]" class="fa fa-spinner fa-spin" style="font-size:48px;"/>
                              <b-tooltip :target="`checkout-dismiss-${item.patNum}`" triggers="hover">
                                Dismiss
                              </b-tooltip>
                            </li> 
                          </ul>
                        </footer>
                      </div>
                    </widget>
                </draggable>              
            </b-col>
            <b-col sm="1" md="1" lg="2" xl="2">
                <div>
                  <h5 class="mt-2 mb-2 fw-bold"><i class="fa fa-bullhorn"/>Alerts</h5>
                </div>
                <div v-if="pp.alerts && pp.alerts.length > 0">
                  <Widget class="ppalert" :title="customAlertHeader(alert.opName)" v-for="(alert, index) in pp.alerts" :key="`alert-${alert.createdAt.seconds}-${index}`" :style="getProviderColorStyle(alert.pingToEmployeeID)" @close-widget="_deleteAlert(alert, index)" customHeader close >
                    <div class="alertbody">
                      <hr>
                      <div class="clearfix fs-mini">
                        <span class="fw-semi-bold mb-2">{{ employeeName(alert.pingToEmployeeID) }} ( {{ getAlertTimeStamp(alert.createdAt) }} )</span>
                      </div>
                      <div class="clearfix fs-mini">
                        <span class="fs-mini fw-semi-bold">{{ alert.msg }}</span>
                      </div>
                      <div class="clearfix fs-mini mt-3">
                        <span v-if="alertTimers.length > 0 && alertTimers[index] && alertTimers[index].elapsed" class="fs-mini fw-semi-bold">Elapsed Time:</span>
                      </div>
                      <div class="clearfix fs-mini">
                        <span v-if="alertTimers.length > 0 && alertTimers[index] && alertTimers[index].elapsed" class="fs-mini fw-semi-bold">{{ alertTimers[index].elapsed }}</span>
                      </div>
                    </div>
                  </Widget>
                </div>  
                <div v-else class="noalert">
                  <span>No Alerts</span>
                </div>
            </b-col>
          </b-row>

          <!-- Chat Section -->
          <div v-if="userID && practicePulseSettings && practicePulseSettings.showChat && practicePulseSettings.showChat !== 'FALSE'" class="chat-section-wrapper">
            <chat-container mode="embedded" :current-user-id="userID" />
          </div>
        </b-container>

        <b-modal ref="ping" id="ping" title="Send Alert" body-class="bg-white">
          <div v-if="alertsGroupByEmployee">
            <div v-for="(value, key, index) in alertsGroupByEmployee" :key="`customer-alert-${key}-${index}`" >
              <span>{{ key }}</span>
              <div v-for="(alert, index) in alertsGroupByEmployee[key]" :key="`customer-alert-${index}`" >
                <b-button              
                  @click="startPingWithShortcut(alert)"
                  variant="success" 
                  size="lg" class="mb-xs mr-xs">
                  {{ alert.alertText }}
                </b-button>
              </div>
            </div>           
          </div>

          <p v-if="alertsShortcutsAvailable" class="mt-3 bold">OR</p>

          <p>Select a User</p>
          <multiselect
            v-if="activeEmployees"
            v-model="selectedEmployee"
            :options="activeEmployees"
            :multiple="false"
            :taggable="false"
            :show-labels="false"
            :custom-label="employeeCustomLabel"          
            track-by="EmployeeID"
            aria-placeholder="Select an Employee"
          >
            <template slot="singleLabel" slot-scope="props">
              <span class="custom__tag">
                <img
                  class="option__image"
                  src="../../assets/icons-user-40.png"
                  alt="Team member image"
                />
                <span>{{ employeeCustomLabel(props.option) }}</span>
                <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
              </span>
            </template>
            <template slot="option" slot-scope="props">
              <img
                class="option__image"
                src="../../assets/icons-user-40.png"
                alt="Team member image"
              />
              <div class="option__desc">
                <span class="option__title">{{ employeeCustomLabel(props.option) }}</span>
              </div>
            </template>
          </multiselect>
          
          <textarea v-model="msg" rows="6" max-rows="6" cols="10" name placeholder="Optional Message goes here..." class="form-control mt-5" ref="msg"></textarea>

          <div slot="modal-footer" class="w-100">
            <b-button variant="danger" size="sm" class="float-right" @click="hideModal('ping')">Cancel</b-button>
            <b-button variant="success" size="sm" class="float-right mr-3" @click="startPing">Send</b-button>
          </div>
        </b-modal> 
    </div>

    <b-modal ref="reset-pp-confirm" size="sm" body-class="bg-white" title="Are you sure?" @ok="deletePP" @cancel="hideModal('reset-pp-confirm')">
            Are you sure? This will clear out everything related to practice-pulse.
    </b-modal>

    <!-- The page's one slideout outlet. v-if (never v-show) because PracticeOpsPage.vue `extends` this file, which makes it
         importable by another .vue — cypress/node-tests/slideout-outlet-guard.test.js then requires the outlet to be gated at the tag.
         Always true here; the redesign renders its own template (and its own single outlet), so this one is simply not mounted. -->
    <slideout-panel v-if="mountsSlideoutOutlet"></slideout-panel>
  </div>

</template>
  
  <script>
  /* eslint-disable */

  /*

    Instructions for our clients on how to allow audio to play on browsers
    https://championcr.com/topic/enable-auto-play/#:~:text=Mac%2C%20and%20Firefox.-,Google%20Chrome,)%E2%80%9D%20to%20%E2%80%9CAllow%E2%80%9C.

  */

  var patientEventIntervalId, alertEventIntervalId, readyIntervalId;

  import Vue from 'vue';
  import draggable from "vuedraggable";
  import Widget from '@/components/Widget/Widget';
  import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
  import mixin from '../../mixin.js';
  import ppmixin from './Ppmixin.js';
  import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
  import Multiselect from "vue-multiselect";
  import moment from 'moment-timezone';
  import ChatContainer from '@/pages/Chat/ChatContainer.vue';
  import ConfigurePracticePulse from "./ConfigurePracticePulse";
  import ShowPracticePulseDetails from "./ShowPracticePulseDetails";
  import CheckoutNotes from './CheckoutNotes';
  import PracticePulseSkeleton from './PracticePulseSkeleton';
  import Push from 'push.js';
  // Elapsed-time maths lives in one tested place. Comparisons use TOTAL minutes; display keeps the
  // hour split. Both used to be computed inline here with `difference % (1000*60*60)`, which is
  // minutes-WITHIN-the-hour — right for display, wrong for a threshold. See src/utils/waitElapsed.js.
  import {
    elapsedMs,
    isOverThreshold,
    formatAlertElapsed,
    formatPatientElapsed,
    waitAlertEnabled,
  } from '@/utils/waitElapsed';
  // What a drag means (reorder / seat / check-out / back to check-in) — see src/utils/practiceOps/moves.js.
  import { resolveDragMove } from '@/utils/practiceOps/moves';

  export default {
    name: "PracticePulse",
    display: "Practice Ops",    
    components: {
      draggable,
      Widget,
      ErrorDisplay,
      Multiselect,
      ChatContainer,
      PracticePulseSkeleton
    },
    mixins: [mixin, ppmixin],

    created() {      
      //console.log('created');
      this.init();
    },

    mounted() {
      //console.log('mounted');
      this.intervalEvents.map(intervalEvent => {
        clearInterval(intervalEvent)
      });

      this.alertIntervalEvents.map(intervalEvent => {
        clearInterval(intervalEvent)
      });

      // Advance the clock the waiting-limit count is derived from. 30s is well under any usable
      // threshold (the configured sites use 5 minutes) and costs one timer for the whole board.
      // Registered in intervalEvents so the existing beforeDestroy teardown clears it.
      this.nowTickIntervalId = setInterval(() => { this.nowTick = Date.now(); }, 30000);
      this.intervalEvents.push(this.nowTickIntervalId);
    },

    beforeDestroy() {
      this.intervalEvents.map(intervalEvent => {
        clearInterval(intervalEvent)
      });

      this.alertIntervalEvents.map(intervalEvent => {
        clearInterval(intervalEvent)
      });

      // The allPatNums watcher opens a Firestore onSnapshot (tasks/getTasksTaggedWithPatients).
      // Without this it outlives the page and keeps writing the shared Vuex slot.
      this.teardownPatientTasksListener();
    },

    data() {
      return {
        // See the <slideout-panel> at the bottom of the template. Always true on the classic page.
        mountsSlideoutOutlet: true,
        dismissInProgress: {},
        color: 'red',
        refreshKey: false,
        alertTimers: [],
        intervalEvents: [],
        alertIntervalEvents: [],
        patientTimers: {},
        visualAlerts: {},
        // Reactive clock for howManyWaitingForMoreThanX, which is derived from how long each
        // checked-in patient has been ready rather than from the persisted visualAlerts map. One
        // board-level tick, NOT one per patient — the per-patient second-by-second intervals only
        // exist for the "Waiting: …" badge and do not cover every patient the count considers.
        nowTick: Date.now(),
        nowTickIntervalId: null,
        allPatNums: [],
        taggedTasks: {},  // object with keys of PatNum that has all the tagged tasks for this patient. This was to make it easy to watch tasks and update this     
        ppLoaded: false,
        drag: false,   
        pp: {},
        ppID: null,
        selectedOp: null,
        selectedOpName: null,
        moveFrom: null,
        moveTo: null,
        moveDisabled: false,
        selectedAptNum: null,
        selectedPatNum: null,
        selectedPatientName: null,        
        selectedEmployee: null,
        msg: null,
        wikiPages: [],
        // Track pending drag operation to validate Firebase updates and prevent stale data overwrites
        pendingDragOperation: null,  // { patNum, from, to }

        // Holds the unsubscribe fn returned by tasks/getTasksTaggedWithPatients so the
        // allPatNums watcher and beforeDestroy() can tear that Firestore listener down.
        // null when no listener is live.
        patientTasksUnsubscribe: null,
        // Bumped on every re-subscribe so a slow dispatch resolution can tell it has been
        // superseded and must not keep its listener. Same guard as
        // src/pages/PatientProfile/components/PatientTasksCard.vue::subscribe().
        patientTasksSubscribeToken: 0,

        // Chat is now handled by ChatContainer component with mode="embedded"        
      };
    },
    methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
      ...mapActions('practicepulse', ['getFbOps', 'getPracticePulseOps', 'sendAlert', 'deleteAlert', 'updateAlerts', 'updateOps', 'updateODConfirmStatus', 'saveDismissedStatus', 'savePracticePulseSettings']),
      ...mapActions('customer', ['getEmployees']),
      ...mapActions('wiki', ['getWikiPages', 'getSiteTags']),
      ...mapActions('tasks', ['getTasksTaggedWithPatients']),

      ...mapMutations('customer', ['setPrivacyOn', 'setSettings']),
      
      async init() {
        //this.downloadSound('https://tpenner.github.io/smw_coin.wav');        
        this.resetError();

        await this.getFbOps({siteID: this.siteID});
        
        /*        
        let temp = await this.getPracticePulseOps({siteID: this.siteID, _date: moment().format('YYYY-MM-DD')});
        Vue.set(this.pp, 'allOps', temp);
        */
        //console.log('in init got ops from OD, this.pp.allOps', this.pp.allOps);
      },

      // Tear down the Firestore listener opened by tasks/getTasksTaggedWithPatients.
      // Mirrors CheckoutNotes.vue::teardownPatientTasksListener() and
      // PatientTasksCard.vue::teardown(): unsubscribe, drop it from the customer store's
      // listener registry, then null the field. The unsubscribe call is guarded so a throw
      // can never leave a dead fn in place.
      //
      // NOTE: deliberately does NOT commit tasks/resetTasksTaggedWithPatients. The
      // allPatNums watcher tears down and re-subscribes on EVERY check-in/out change, and
      // blanking the shared slot in between would flicker the task badges here and in
      // ShowPracticePulseDetails.vue, which reads the same slot. The action's own snapshot
      // handler already resets the slot on every snapshot.
      teardownPatientTasksListener() {
        if (typeof this.patientTasksUnsubscribe === 'function') {
          try {
            this.patientTasksUnsubscribe();
            this.$store.commit('customer/removeFirestoreListener', this.patientTasksUnsubscribe);
          } catch (err) {
            console.log('PracticePulse: error tearing down patient tasks listener', err);
          }
        }
        this.patientTasksUnsubscribe = null;
      },

      showPPHelp() {        
        window.open('https://teamioapp.notion.site/Practice-Pulse-f7d58ad41e384c23b9b80f46c68d0ee2', '_blank');
      },

      // The shared "commit a move" step for a drag-and-drop (onEnd) and the Move to Op menu
      // (handleMoveFromCheckin). It runs AFTER the card is already in its new local column — draggable
      // has moved it, or handleMoveFromCheckin spliced it — and `patient` is that card.
      //   seat     (to an operatory): stamp dateTimeTreatmentStarted ONCE, clear the ready timer, write the
      //            board, then the In-Treatment status to OD (with event + operatoryNum for the backend)
      //   checkout (to Check-out):    open CheckoutNotes; nothing is written until its Save
      //   checkin  (back to Check-in): write the board only — it is NOT a seating, so no In-Treatment status
      async moveCard(patient, from, to) {
        const move = resolveDragMove({ from, to });
        if(!move || !patient) return;

        const patNum = patient.patNum;
        const aptNum = patient.aptNum;

        // Practice Ops ("Room open · last patient left N min ago"): a card that LEAVES an operatory — for Check-out,
        // or back to Check-in — remembers which room it left. board.js reads `lastRoom` from the Check-out cards.
        // Stamped before the write below (and before CheckoutNotes opens), so the board document carries it.
        if((move.kind === 'checkout' || move.kind === 'checkin') && /^\d+$/.test(String(move.from))) {
          Vue.set(patient, 'lastRoom', Number(move.from));
        }

        if(move.kind === 'checkout') {
          const patientIndex = this.getPatientIndexInOp(move.to, patNum);
          this.checkoutNotes(move.from, patient, patientIndex, true);
          return;
        }

        if(move.kind === 'seat') {
          // Only the FIRST seating stamps the time: moving a seated patient between operatories must not
          // re-stamp it, or the report's wait/treatment times restart at the room change.
          if(!patient.dateTimeTreatmentStarted) {
            Vue.set(patient, 'dateTimeTreatmentStarted', moment().toDate());
          }

          // let's clear the interval if there are any timers for this patient set
          if(this.patientTimers[patNum] && this.patientTimers[patNum].readyIntervalId) {
            let clearId = this.patientTimers[patNum].readyIntervalId;
            this.removeIntervalId(clearId);

            delete this.visualAlerts[patNum];
            Vue.delete(this.visualAlerts, patNum);

            delete this.patientTimers[patNum];
            Vue.delete(this.patientTimers, patNum);
          }
        }

        let _checkin = {};

        for(const _item of this.pp.ops['checkin']) {
          _checkin[_item.patNum] = _item;
        }

        let payload = {
          ppFBID: this.practicePulse.id,
          ops: {
            ...this.pp.ops,
            checkin: _checkin
          }
        }

        // as long as the card came from Check-in send the visual alerts also
        if(move.from === 'checkin') {
          payload.visualAlerts = this.visualAlerts;
          payload.deletedCheckinKeys = [patNum];
        }

        await this.updateOps(payload);

        if(move.kind === 'seat') {
          // now let's also update this status in their local OD so its synced
          await this._updateODConfirmStatus(aptNum, this.practicePulseSettings.inTreatmentRoomStatus.DefNum, {
            event: 'seated',
            operatoryNum: Number(move.to)
          });
        }
      },

      async handleMoveFromCheckin(item, opNum, aptNum, patNum) {
        //console.log('in handleMoveFromCheckin', item, opNum, aptNum, patNum);
        if(!item.dateTimeReady) {
          this.$toast.error('You cannot move this patient yet without marking the patient appointment "ready" (or "Ready to go back" equivalent) in Open Dental first');
          return false;
        }

        // Block Firebase updates during this operation (same as drag-and-drop)
        window.__isDragging = true;

        let moveFromOpID = 'checkin';
        let moveToOpID = opNum;

        this.selectedAptNum = aptNum;
        this.selectedPatNum = patNum;

        // Track pending operation to validate Firebase updates
        this.pendingDragOperation = {
          patNum: patNum,
          from: moveFromOpID,
          to: moveToOpID
        };

        //console.log('this.pp.ops', this.pp.ops);

        let patientIndexInCheckin = this.getPatientIndexInOp('checkin', this.selectedPatNum);
        //console.log('patientIndexInCheckin', patientIndexInCheckin);

        // Move the card locally (draggable does this itself on a drag), then commit it the same way.
        const card = this.pp.ops['checkin'][patientIndexInCheckin];
        this.pp.ops[opNum].push(card);
        this.pp.ops['checkin'].splice(patientIndexInCheckin, 1);

        //console.log('this.pp.ops', this.pp.ops);

        await this.moveCard(card, moveFromOpID, moveToOpID);

        // Clear drag flag after delay to allow Firebase to sync
        setTimeout(() => {
          window.__isDragging = false;
          this.pendingDragOperation = null;
        }, 1500);
      },

      async dismiss(item, index) {
        //console.log('in dismiss', item, index);
        this.dismissInProgress[item.patNum] = true;

        this.selectedAptNum = item.aptNum;       
        
        this.pp.ops.checkout[index].checkinDefNum = this.practicePulseSettings.dismissedStatus.DefNum;
        this.pp.ops.checkout[index].checkinStatus = this.practicePulseSettings.dismissedStatus.ItemValue;
        this.pp.ops.checkout[index].dateTimeDismissed = moment().toDate();        
        

        //Vue.set(this.pp.ops.checkout[index], 'checkinDefNum', this.practicePulseSettings.dismissedStatus.DefNum);
        //Vue.set(this.pp.ops.checkout[index], 'checkinStatus', this.practicePulseSettings.dismissedStatus.ItemValue);

        let existingCheckout = this.pp.ops.checkout;
        existingCheckout[index].checkinDefNum = this.practicePulseSettings.dismissedStatus.DefNum;
        existingCheckout[index].checkinStatus = this.practicePulseSettings.dismissedStatus.ItemValue;
        existingCheckout[index].dateTimeDismissed = moment().toDate();        

        let _checkin = {};

        for(const _item of this.pp.ops['checkin']) {
            //console.log('item', _item);
            _checkin[_item.patNum] = _item;
        }

        let payload = {
          ppFBID : this.practicePulse.id,
          ops: {
            ...this.pp.ops,
            checkout: existingCheckout,
            checkin: _checkin            
          }
        }
        //console.log('payload in dismiss', payload);

        const p1 = this.updateOps(payload);
        const p2 = this.saveDismissedStatus({siteID: this.siteID, aptNum: this.selectedAptNum});
        const p3 = this._updateODConfirmStatus(this.selectedAptNum, this.practicePulseSettings.dismissedStatus.DefNum);
        const allPromises = Promise.all([p1,p2,p3]);

        allPromises.then(values => {
          this.dismissInProgress[item.patNum] = false;
        }).catch(error => {
          console.log(error);
          this.dismissInProgress[item.patNum] = false;
        });

        /*
        await this.updateOps(payload);
        await this.saveDismissedStatus({siteID: this.siteID, aptNum: this.selectedAptNum});        
        this.dismissInProgress = false;
        // now let's also update this status in their local OD so its synced
        await this._updateODConfirmStatus(this.selectedAptNum, this.practicePulseSettings.dismissedStatus.DefNum);
        */
        
      },

      async refreshPP2() {
        let temp = await this.getPracticePulseOps({siteID: this.siteID, _date: moment().format('YYYY-MM-DD')});
        
        //Vue.set(this.pp, 'allOps', temp);

        // this are all the opnums from mysql
        let allOpNums = _.map(temp, 'OperatoryNum');
        console.log('allOpNums', allOpNums);

        // now check local opnums from fb. whatever is missing, add it there and save to fb        
        let {checkin, checkout, ...rest} = this.pp.ops;
        //console.log('rest', rest);

        let existingOps = _.map(Object.keys(rest), function(n) {
          return parseInt(n,10);
        });

        console.log('existingOps', existingOps);

        // now take the diff of allOpNums and rest
        let diff = _.difference(allOpNums, existingOps);
        console.log('diff', diff);
        
        for(const e of diff) {
          this.pp.ops[e] = [];
        }        
        
        let payload = {
          ppFBID : this.practicePulse.id,
          ops: this.pp.ops
        }

        console.log('payload in refreshPP', payload);
        await this.updateOps(payload);        
      },

      async refreshPP() {
        let allOpsFromDB = await this.getPracticePulseOps({siteID: this.siteID, _date: moment().format('YYYY-MM-DD')});
        let allOpsFromSettings = this.practicePulseSettings.allOps;

        let allOps = _.unionBy(allOpsFromDB, allOpsFromSettings, "OperatoryNum");

        console.log('allOpsFromDB', allOpsFromDB);
        console.log('allOpsFromSettings', allOpsFromSettings);
        console.log('allOps', allOps);

        Vue.set(this.practicePulseSettings, 'allOps', allOps);

        if(this.allSettings.PRACTICE_PULSE_SETTINGS && this.allSettings.PRACTICE_PULSE_SETTINGS.PropertyValue !== '') {
          let temp = this.allSettings.PRACTICE_PULSE_SETTINGS.PropertyValue;
          temp = temp.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
          let existingSettingObj = JSON.parse(temp);
          console.log('existingSettingObj', existingSettingObj);
          existingSettingObj.allOps = allOps;
          console.log('after existingSettingObj', existingSettingObj);
          Vue.set(this.allSettings, 'PRACTICE_PULSE_SETTINGS', JSON.stringify(existingSettingObj));
        }        

        let payload = {
            ppFBID : this.practicePulse.id,
            siteID: this.siteID,
            alerts: this.pp.alerts,
            ops: {              
              checkout: this.pp.ops.checkout
            },
            //visualAlerts: admin.firestore.FieldValue.delete()
            visualAlerts: this.visualAlerts
        }

        let _checkin = {};

        for(const _item of this.pp.ops['checkin']) {
            //console.log('item', _item);
            _checkin[_item.patNum] = _item;
        }

        payload.ops.checkin = _checkin;
        
        for (const op of allOps) {
            if(this.pp.ops[op.OperatoryNum]) {
              payload.ops[op.OperatoryNum] = this.pp.ops[op.OperatoryNum];
              this.practicePulseSettings.ops[op.OperatoryNum] = this.pp.ops[op.OperatoryNum];
            }
            else {
              payload.ops[op.OperatoryNum] = [];
              this.practicePulseSettings.ops[op.OperatoryNum] = [];
            }
        }

        Vue.set(this.practicePulseSettings, 'ops', this.practicePulseSettings.ops);        
        this.$forceUpdate();        

        let payload2 = {
          siteID: this.siteID,
          practicePulseSettings: this.practicePulseSettings
        }        

        console.log(`payload in refreshPP: ${JSON.stringify(payload, null, 2)}`);        
        await this.updateOps(payload);
        console.log('ops updated');
        await this.savePracticePulseSettings(payload2);
        console.log('practice pulse settings saved to db');
      },

      async deletePP() {        
        this.intervalEvents.map(intervalEvent => {
          clearInterval(intervalEvent)
        });

        this.alertIntervalEvents.map(intervalEvent => {
          clearInterval(intervalEvent)
        });

        this.visualAlerts = [];
        this.pp.alerts = [];

        let temp = {
          checkin: {}
        };

        for(const item in this.pp.ops) {          
          if(item !== 'checkin') {
            temp[item] = [];
          } 
        }

        let payload = {
          ppFBID : this.practicePulse.id,
          ops : temp,
          visualAlerts: null,
          alerts: []
        }
        await this.updateOps(payload);
      },

      async _deleteFromCheckin(item, index) {
        this.$delete(this.pp.ops.checkin, index);

        this.selectedPatNum = item.patNum;
        //console.log('in _deleteFromCheckin, selectedPatNum', this.selectedPatNum);

        if(this.patientTimers[this.selectedPatNum]) {
          // ⚠️ The interval MUST be cleared before the entry is dropped, the way
          // handleMoveFromCheckin already does it. Without this the orphaned interval kept running
          // and re-created patientTimers[patNum] a second later (updatePatientTime ends in a
          // Vue.set), which then made the `if(!this.patientTimers[patNum])` arming guard in the
          // Firestore watcher permanently false — so if that patient ever came back to check-in,
          // no fresh timer was armed and the zombie kept counting from the original ready time.
          if(this.patientTimers[this.selectedPatNum].readyIntervalId) {
            this.removeIntervalId(this.patientTimers[this.selectedPatNum].readyIntervalId);
          }
          //delete this.patientTimers[this.selectedPatNum];
          Vue.delete(this.patientTimers, this.selectedPatNum);
          //console.log('deleted patienttimer for patnum');
        }

        if(this.visualAlerts[this.selectedPatNum]) {
          //delete this.visualAlerts[this.selectedPatNum];                        
          Vue.delete(this.visualAlerts, this.selectedPatNum);
          //console.log('deleted visual alert for patnum');
        }

        let _checkin = {};

        for(const _item of this.pp.ops['checkin']) {
          //console.log('item', _item);
          _checkin[_item.patNum] = _item;
        }
        
        let payload = {
          ppFBID : this.practicePulse.id,
          ops: {
            ...this.pp.ops,
            checkin: _checkin
          }          
        }

        payload.visualAlerts = this.visualAlerts;
        payload.deletedCheckinKeys = [item.patNum];

        //console.log('in deleteAlert, payload', payload);
        await this.updateOps(payload);
        
      },

      async _deleteAlert(alert, index) {
        /*
        console.log('in _deleteAlert, alert', alert);
        console.log('in _deleteAlert, index', index);

        let payload = {
          ppFBID : this.practicePulse.id,
          alert: alert
        }
        console.log('in _deleteAlert, payload', payload);
        await this.deleteAlert(payload);
        console.log('deleted');
        */

        //console.log('in _deleteAlert, index', index);
        //this.pp.alerts.splice(index,1);
        
        this.$delete(this.pp.alerts, index);

        //Vue.set(this.pp, 'alerts', this.pp.alerts.splice(index,1));

        let payload = {
          ppFBID : this.practicePulse.id,
          alerts: this.pp.alerts
        }
        //console.log('in deleteAlert, payload', payload);
        await this.updateAlerts(payload);
      },

      getOpIdFromName(opName) {
        if(this.pp.allOps && this.pp.allOps.length > 0) {
          let op = this.pp.allOps.find(o => o.OpName === opName);
          if(op)
            return op.operatoryNum;
        }
      },

      getPatientIndexInOp(opID, patNum) {
        //console.log(`in getPatientIndexInOp, opID: ${opID}, patNum: ${patNum}`);
        //console.log('this.pp.ops', this.pp.ops);
        if(this.pp.ops[opID]) {          
          return this.pp.ops[opID].findIndex(p => p.patNum === patNum);        
        }
      },

      updateAlertTime(index, startTime) {
        // 🔴 FIXED 2026-09-21. The old inline version wrote the hour part and then overwrote the
        // WHOLE string with 'less than a minute' whenever minutes-within-the-hour was 0 — so an
        // unanswered summon standing for exactly 1, 2 or 3 hours displayed 'less than a minute'.
        // On a feature whose entire promise is "a clock tells you nobody came", that is the clock
        // lying in the only direction that matters. formatAlertElapsed keeps the wording (including
        // the literal 'minute(s)') and reserves 'less than a minute' for a real sub-minute wait.
        Vue.set(this.alertTimers, index, {
          elapsed: formatAlertElapsed(elapsedMs(startTime)),
        });
      },

      setAlertTimers() {
        if(this.pp && this.pp.alerts) {  
          //console.log('in setAlertTimers, found alerts, now setting up alerts');               
          let _this = this;          
          let index = 0;
          for(const alert of this.pp.alerts) {
            //console.log(alert);
            let temp = moment(alert.createdAt.toDate());
            //console.log('temp valueof', temp.valueOf());            
            alertEventIntervalId = setInterval(_this.updateAlertTime.bind(null,index,temp.valueOf()),1000);
            //_this.intervalEvents.push(alertEventIntervalId);
            _this.alertIntervalEvents.push(alertEventIntervalId);            
            index++;
          }          
        }
      },

      updatePatientTime(patNum, startTime) {
        // 🔴 FIXED 2026-09-21 — the threshold compared minutes-WITHIN-THE-HOUR against a flat
        // minute setting, so a 15-minute limit went silent from 60 to 74 minutes (and again from
        // 120 to 134, and so on), and any limit of 60+ could never fire at all. Display still wants
        // the hour split; the comparison wants TOTAL minutes. Both now come from waitElapsed.js.
        const ms = elapsedMs(startTime);
        const elapsed = formatPatientElapsed(ms);

        // ⚠️ waitAlertEnabled checks BOTH halves of the setting. The old condition read only
        // `elapsedTime`, so a site that unticked "Show visual alert if more than…" kept firing on
        // the orphaned number the config screen leaves behind (it only :disables the input).
        if(waitAlertEnabled(this.practicePulseSettings) &&
           isOverThreshold(ms, this.practicePulseSettings.elapsedTime)) {
          if(!this.visualAlerts[patNum] && this.isPatientInCheckin(patNum)) {
            let msg = `this patient has been waiting for more than ${this.practicePulseSettings.elapsedTime}`;
            Vue.set(this.visualAlerts, patNum, msg);
            this.updateTimersInFB();
          }
        }

        let obj;

        if(!this.patientTimers[patNum]) {
          obj = {
            elapsed
          }
        } else {
          obj = {
            ...this.patientTimers[patNum],
            elapsed
          }
        }
        Vue.set(this.patientTimers, patNum, obj);
      },

      /*
      setPatientTimers() {
        if(this.pp && this.pp.ops) {                 
          let _this = this;          
          let index = 0;
          for (const op in this.pp.ops) {
            for (const patient of this.pp.ops[op]) {
              if(patient.start) {              
                let temp = moment(patient.start.toDate());
                //console.log('temp', temp);
                patientEventIntervalId = setInterval(_this.updatePatientTime.bind(null,patient.patNum,temp.valueOf()),1000);
                _this.intervalEvents.push(patientEventIntervalId);
                index++;
              }
            }
          }                 
        }
      },
      */

      /*
      setReadyTimer() {
        if(this.pp && this.pp.ops) {                 
          let _this = this;          
          let index = 0;
          for (const op in this.pp.ops) {
            for (const patient of this.pp.ops[op]) {
              if(patient.readyStart) {              
                let temp = moment(patient.readyStart.toDate());
                //console.log('temp', temp);
                readyIntervalId = setInterval(_this.updatePatientTime.bind(null,patient.patNum,temp.valueOf()),1000);
                _this.intervalEvents.push(readyIntervalId);
                index++;
              }
            }
          }                 
        }
      },
      */

      getAlertTimeStamp(d) {
        return moment(d.toDate()).format('hh:mma');
      },

      employeeCustomLabel({Name}) {      
        return `${Name}`;
      },

      customAlertHeader(opName){
        return `<h6><span class='fw-semi-bold'>${opName} </span></h6>`;
      },

      /*
      showWiki() {
        var _this = this;
        try {
          const showWikiHandle = this.$showPanel({
            component : ShowWiki,
            openOn: 'right',
            width: 1050,
            disableBgClick: true,
            disableEscClick: true,
            props: {
              wikiPageID: '6fdb1432-a57e-4be6-a230-9788d7793cc7'
            }
          });

          showWikiHandle.promise.then(async(result) => {
            //console.log(`Got result: ${JSON.stringify(result, null, 4)}`);            
          });

        } catch(err) {
          console.log(err);
        }
      },
      */

      showDetails(detailType, detailData) {
        var _this = this;
        try {
          const showDetailsHandle = this.$showPanel({
            component : ShowPracticePulseDetails,
            openOn: 'right',
            width: 1350,
            disableBgClick: true,
            disableEscClick: true,
            props: {
              detailType,
              detailData
            }
          });

          showDetailsHandle.promise.then(async(result) => {
            //console.log(`Got result: ${JSON.stringify(result, null, 4)}`);            
          });

        } catch(err) {
          console.log(err);
        }
      },

      configurePracticePulse() {        
        var _this = this;
        try {
          const configurePracticePulseHandle = this.$showPanel({
            component : ConfigurePracticePulse,
            openOn: 'right',
            width: 1850,
            disableBgClick: true,
            disableEscClick: true,
            props: {
              practicePulseSettings: _this.practicePulseSettings || {},
              pp: this.pp
            }
          });

          configurePracticePulseHandle.promise.then(async(result) => {
            //console.log(`Got configure result: ${JSON.stringify(result, null, 4)}`);
            if(this.allSettings && !this.allSettings.PRACTICE_PULSE_SETTINGS) {
              let temp = {
                Property: 'PRACTICE_PULSE_SETTINGS',
                PropertyValue:JSON.stringify(result)
              }
              Vue.set(this.allSettings, 'PRACTICE_PULSE_SETTINGS', temp);              
            } else
              Vue.set(this.allSettings.PRACTICE_PULSE_SETTINGS, 'PropertyValue', JSON.stringify(result));
            Vue.set(this, 'practicePulseSettings', result);
            this.refreshKey = !this.refreshKey;
          });

        } catch(err) {
          console.log(err);
        }
      },

      checkoutNotes(moveFromOpID, item, index, cancelNeeded) {
        //console.log('in checkout notes, moveFromOpID, item, index, cancelNeeded', moveFromOpID, item, index, cancelNeeded);
        var _this = this;

        try {
          const checkoutNotesHandle = this.$showPanel({
            component : CheckoutNotes,
            openOn: 'right',
            width: 1850,
            disableBgClick: true,
            disableEscClick: true,
            props: {
              aptNum: item && item.aptNum ? item.aptNum : this.selectedAptNum,
              patientNum: item && item.patNum ? item.patNum : this.selectedPatNum,
              patientName: item ? item.name : this.selectedPatientName,
              ppID: this.ppID,
              practicePulseSettings: this.practicePulseSettings,
              patientItem: item
            }
          });

          checkoutNotesHandle.promise.then(async(result) => {
            // if this was called from checkout already then we don't need to do anything
            if(moveFromOpID !== 'checkout') {
              //console.log(`Got result from checkout notes popup: ${JSON.stringify(result, null, 4)}`);              
              if(result) {
                _this.ppID = result.ppID;
                if(result.checkout) {
                  _this.checkout(item, moveFromOpID, index, cancelNeeded);
                } else {                  
                  if(cancelNeeded) {
                    //console.log('cancel needed. tryng to cancel checkout');
                    _this.cancelCheckout(moveFromOpID, item, index);
                  }
                }
              } 
            }           
          });

        } catch(err) {
          console.log(err);
        }
      },

      downloadSound(soundObj) {
        let name = soundObj.name;
        let url = soundObj.url;
        let fileType = soundObj.type; 

        if(!url) {
          console.log(`in downloadSound, url: ${url}`);
          //url = 'https://tpenner.github.io/smw_coin.wav';        
          //url = 'http://localhost:8080/app/practice-pulse/sounds/elevator-ding.mp3'
          url = 'https://teamio-frontend.s3.us-east-1.amazonaws.com/sounds/call-to-attention-123107.mp3';
        }
        //console.log('url', url);
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.onload = function(e) {
          if (this.status == 200) {            
            var blob = new Blob([this.response], {
              type: fileType
            });
            var reader = new window.FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = function() {
              var urlB64 = reader.result;
              var myStorage = localStorage.setItem(`${name}_base64`, urlB64);
            }
          }
        };
        xhr.send();
      },

      /*
        I keep getting this error
        DOMException: play() failed because the user didn't interact with the document first. https://goo.gl/xX8pDD
      */

      playSound(soundObj) {
        var context = new AudioContext();
        //console.log('audio context state', context.state);
        if(context.state === 'suspended') {
          /*
          if (window.confirm("Do you want to turn on sound notifications?")) {
            context = new AudioContext();
            context.resume().then(() => {
              this.playFile(soundObj);
            });
          }
          */
          alert('Please enable Sound for this site first. You will not be able to hear any sound notifications otherwise.');
        } else {
          context.resume().then(() => {
            this.playFile(soundObj);
          });
        }
      },

      playFile(soundObj) {
        let name = soundObj.name;
        let url = soundObj.url;
        let fileType = soundObj.type; 

        var soundB64 = localStorage.getItem(`${name}_base64`);
        //console.log('soundB64', soundB64);
        var sound = new Audio(soundB64);
        let promise = sound.play();
        if (promise !== undefined) {
          promise.then(_ => {
            // Autoplay started!
            //console.log('played sound');
          }).catch(error => {
            // Autoplay was prevented.
            // Show a "Play" button so that user can start playback.
            console.error(error);
          });
        }
      },

      showModal(name) {        
        this.$refs[name].show();
      },

      hideModal(ref) {
        this.$refs[ref].hide();
      },

      ping(id) {
        //console.log('in ping, id', id);
        this.selectedOpID = id;
        let op = this.practicePulseSettings.allOps.find(o => parseInt(o.OperatoryNum,10) === parseInt(id,10));
        if(op) {
          this.selectedOpName = op.OpName;
          //console.log('selectedOpName', this.selectedOpName);
        }
        this.showModal('ping');
      },

      startPing: async function() {
        if(!this.selectedEmployee) {          
          this.$toast.error('Please select a user first');
          return false;
        }

        this.hideModal('ping');

        let alert = {          
          pingFromEmployeeID: this.userID,
          pingToEmployeeID: this.selectedEmployee.EmployeeID,
          opID: this.selectedOpID,
          opName: this.selectedOpName,
          msg: this.msg,
          createdAt: moment().toDate(),
          elapsed: ''
        }

        let payload = {
          alert,
          ppFBID: this.practicePulse.id
        }
        
        //console.log('alert payload', payload);
        await this.sendAlert(payload);
        this.selectedEmployee = null;
        this.msg = null;
      },

      async startPingWithShortcut(item) {
        let emp = item.employee;
        this.hideModal('ping');
        let alert = {          
          pingFromEmployeeID: this.userID,
          pingToEmployeeID: emp.EmployeeID,
          opID: this.selectedOpID,
          opName: this.selectedOpName,
          msg: item.alertText,
          createdAt: moment().toDate(),
          elapsed: ''        
        }

        let payload = {
          alert,
          ppFBID: this.practicePulse.id
        }

        //console.log('alert shortcut payload', payload);
        await this.sendAlert(payload);
        this.selectedEmployee = null;
        this.msg = null;        
      },

      // Event when you move an item in the list or between lists
      onMove: function(e) {
        try {
          let element = e.draggedContext.element;
          
          if(!element.dateTimeReady) {          
            this.moveDisabled = true;          
            this.drag = false;
            window.__isDragging = false; // Clear drag flag on block
            this.$toast.error('You cannot move this patient yet without marking the patient appointment "ready" (or "Ready for treatment" equivalent) in Open Dental first');              
            return false;
          } else {
            this.moveDisabled = false;
            return true; // CRITICAL: Must explicitly return true to allow the drag
          }
        } catch (error) {
          console.error('[DRAG] ERROR in onMove:', error);
          return false;
        }
      },

      // Element dragging started
      onStart: function (e, list) {
        this.moveDisabled = false;
        window.__isDragging = true; // CRITICAL: Block Firestore updates during drag
        this.drag = true;
        // Forget the previous drag. onChange only fills these on a cross-column move, so a reorder
        // inside one column used to inherit the LAST drag's from/to/patient and re-write it.
        this.moveFrom = null;
        this.moveTo = null;
        this.selectedAptNum = null;
        this.selectedPatNum = null;
        this.selectedPatientName = null;
        e.oldIndex;  // element index within parent
      },

      // Called when dragging element changes position
      onChange: function(e, list) {
        if(e.removed) {
          this.moveFrom = list;
          this.selectedAptNum = e.removed.element.aptNum;
          this.selectedPatNum = e.removed.element.patNum;
          this.selectedPatientName = e.removed.element.name;
        }

        if(e.added) {
          this.moveTo = list;
        }
      },

      // Element dragging ended
      onEnd: async function(e, list) {
        try {
          if(this.drag) {
            this.drag = false;

            // Only a card that changed columns is a move. resolveDragMove returns null for a reorder
            // inside one column (moveFrom/moveTo stay null) — then nothing is written at all.
            const move = resolveDragMove({ from: this.moveFrom, to: this.moveTo });

            if(move) {
              // Track the pending drag operation so watcher can validate incoming Firebase data
              this.pendingDragOperation = {
                patNum: this.selectedPatNum,
                from: move.from,
                to: move.to
              };

              // By the time onEnd executes, draggable has already moved the card into the destination
              // column, so look for it THERE (it is no longer in the column it came from).
              const patientIndex = this.getPatientIndexInOp(move.to, this.selectedPatNum);
              const patient = patientIndex >= 0 ? this.pp.ops[move.to][patientIndex] : null;

              if(patient) {
                await this.moveCard(patient, move.from, move.to);
              } else {
                console.error(`[DRAG] moved card ${this.selectedPatNum} not found in ${move.to}; nothing written`);
              }
            }
          }
        } catch(err) {
          console.error('[DRAG] Error in onEnd:', err);
          // Clear pending operation on error so watcher can process updates
          this.pendingDragOperation = null;
        } finally {
          // CRITICAL: Keep drag flag set for 1500ms to allow Firestore round-trip to complete
          // The watcher will also validate incoming data against pendingDragOperation
          // to prevent stale data from overwriting the correct local state
          setTimeout(() => {
            window.__isDragging = false;
            // Clear pending operation after timeout - watcher should have received valid data by now
            this.pendingDragOperation = null;
          }, 1500);
        }
      },

      itemClick: function(e) {
        //console.log("itemClick");
        //console.dir(e);
      },

      isPatientInCheckin(patNum) {
        /*
        console.log('in isPatientInCheckin, patNum', patNum);
        console.log('type of this.pp.ops.checkin', typeof(this.pp.ops.checkin));
        console.log('this.pp.ops.checkin', this.pp.ops.checkin);        
        console.log('this.pp.ops.checkin length', this.pp.ops.checkin.length);
        */
        if(Array.isArray(this.pp.ops.checkin)) {
          let temp = this.pp.ops.checkin.filter(p => p.patNum === parseInt(patNum,10));
          //console.log('temp', temp);
          return temp.length > 0;
        } else {          
          if(this.pp.ops.checkin[patNum])
            return true;
          else
            return false;
        }        
      },

      async updateTimersInFB() {
        let payload = {
          ppFBID: this.practicePulse.id,
          visualAlerts: this.visualAlerts
        }
        //console.log('payload in updateTimersInFB', payload);        
        await this.updateOps(payload);
      },

      async checkout(item, opID, index, cancelNeeded) {
        //console.log('now checking out: item, opID, index', item, opID, index);

        // Practice Ops: remember the room this patient is leaving (see moveCard). The Check-out icon on a room card
        // comes through here with opID = the OperatoryNum; a drag already stamped it in moveCard.
        if(item && /^\d+$/.test(String(opID))) {
          Vue.set(item, 'lastRoom', Number(opID));
        }

        // A DRAG into Check-out (cancelNeeded) has already moved the card into the column, and the stamp below sits in
        // the !cancelNeeded branch — so the card used to be written with no dateTimeCheckout, and the Practice Ops
        // Check-out column could not say "Checkout 9:38". Stamp it ONCE, here.
        if(cancelNeeded && item && !item.dateTimeCheckout) {
          Vue.set(item, 'dateTimeCheckout', moment().toDate());
        }

        if(!cancelNeeded) {
          let temp = [...this.pp.ops[opID]];      // returns a reference to a new array and not the original array
          temp.splice(index, 1);           
          //console.log('temp', temp);          
          let _checkout = [...this.pp.ops['checkout']];
          _checkout.push(item);
          Vue.set(this.pp.ops, opID, temp);
          Vue.set(this.pp.ops, 'checkout', _checkout);
          // The card just pushed (it is `item`). This used to index the Check-out column with the card's index in its
          // ROOM (always 0), which stamped whoever was FIRST in Check-out and left this card without a time.
          Vue.set(item, 'dateTimeCheckout', moment().toDate());
        }       

        /*
          have to change the checkin property and convert that from object of arrays to key-value pair because that's how it needs to be saved in firebase
        */       
        let _checkin = {};          

        for(const _item of this.pp.ops['checkin']) {
          //console.log('item', _item);
          _checkin[_item.patNum] = _item;
        }

        let payload = {
          ppFBID: this.practicePulse.id,
          ops: {
            ...this.pp.ops, 
            checkin: _checkin             
          }
        }

        if(opID === 'checkin') {
          payload.deletedCheckinKeys = [item.patNum];
        }

        //console.log('payload in checkout', payload);
        await this.updateOps(payload);
      },

      cancelCheckout(moveFromOpID, item, index) {
        //console.log('trying to cancel checkout', moveFromOpID, item, index);        

        let from = this.pp.ops[moveFromOpID];
        let to = this.pp.ops['checkout'];

        //console.log('in cancel checkout, from before', from);
        //console.log('in cancel checkout, to before', to);
        
        to.splice(index, 1);
        from.push(item);       

        Vue.set(this.pp.ops, moveFromOpID, from);
        Vue.set(this.pp.ops, 'checkout', to);
      },

      populateInfo(patient) {
        if(!patient.name) {
          let p = this.getPatient(patient.patNum);
          if(p) {
            //console.log(`found patient`, p);
            if(this.privacyOn)
              patient.name = `${p.FName}`;
            else
              patient.name = `${p.FName} ${p.LName}`;
          }
        }        
        return patient;
      },

      getContrastTextColor(hexColor) {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        // W3C relative luminance formula
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
      },

      getProviderColorStyle(employeeID) {
        if(this.practicePulseSettings && this.practicePulseSettings.providerSettings && this.practicePulseSettings.providerSettings[employeeID]) {
          const bgColor = this.practicePulseSettings.providerSettings[employeeID].color;
          const textColor = this.getContrastTextColor(bgColor);
          return `background-color: ${bgColor}; color: ${textColor};`;
        }
        else
          return '';
      },

      getCheckinProvStyle(item) { 
        //console.log(`in getCheckinProvStyle, item:`, item); 
        let employeeID = this.getEmployeeIDFromProvNum(item.provNum);
        // Same guard as getProviderColorStyle: a provider with no settings entry has no color
        // (it used to throw and blank the card).
        if(employeeID !== null && this.practicePulseSettings && this.practicePulseSettings.providerSettings && this.practicePulseSettings.providerSettings[employeeID]) {
            return `border-left: 5px solid ${this.practicePulseSettings.providerSettings[employeeID].color}; background-color:#ffffff;`;
        } else {
          //console.error('Can not look up provider color. Provider not found');
        }
        //return `border-left: 5px solid ${this.numToHex(item.provColor)}; background-color:#ffffff;`;        
      },

      getPatientBalanceStyle(item) {
        if(item.patientBalanceResults && item.patientBalanceResults.length > 0) {
          let amt = parseInt(item.patientBalanceResults[0].PatientAmount, 10);
          if(amt > 0)
            return `color: red`;
          else
            return `color: grey`;
        }
      },

      getRecallStyle(item) {
        let alertNeeded = _.some(item.recallResults, function (r) {
          return r.AptDateTime === null && r.RecallDue !== null;
        });

        if(alertNeeded)
          return `color: red`;
        else
          return `color: grey`;
      },

      getTasksStyle(item) {
        if(this.taggedTasks[item.patNum])
          return `color: red`;
        else
          return `color: grey`;
      },

      getOrthoStyle(item) {
        //let alertNeeded = item.orthoResults && item.orthoResults.length && item.orthoResults.length > 0;
        let alertNeeded = false;

        if(item.orthoResults && item.orthoResults.length && item.orthoResults.length > 0) {
          let result = item.orthoResults[0];
          if(result.AgeLimit !== null && parseInt(result.Age, 10) <= result.AgeLimit) {
            alertNeeded = true;
          }
        }

        if(alertNeeded)
          return `color: red`;
        else
          return `color: grey`;
      },

      isBirthday(patNum) {
        let retVal = false;
        let p = this.getPatient(patNum);
        //console.log('in bday, p', p);
        if(p&& p.Birthdate && p.Birthdate !== '') {
          if(moment().format('YYYY-MM-DD') === moment(p.Birthdate).format('YYYY-MM-DD'))
            retVal = true;          
        }
        //console.log('retVal', retVal);
        return retVal;
      },

      togglePrivacy() {
        //this.privacyOn = !this.privacyOn;
        this.setPrivacyOn(!this.privacyOn);
      },

      createAlertPushNotification(title, body) {
        if(Push.Permission.has()) {
          Push.create(title, {
            body: body && body !== null ? body : '',
            icon: '/images/icon.png',
            link: `${window.location.origin}/app/practice-pulse/`,
            timeout: 20000,
            onClick: function () {
                //console.log("Push notification clicked");
                window.focus();
                this.close();
            },
            vibrate: [200, 100, 200, 100, 200, 100, 200]
          });
        }
      },

      sortCheckin(patients) {
        return _.sortBy(patients, function (p) {          
          return p.aptDateTime.seconds;
        });
      },

      /* Sort checkout such that dismissed people are at the bottom */
      sortCheckout(patients) {
        //console.log('in sortCheckout, patients', patients);
        return _.orderBy(patients, 'checkinStatus', 'desc');
      },

      removeIntervalId(intervalId) {
        clearInterval(intervalId);
        let idx = this.intervalEvents.indexOf(intervalId);
        if(idx !== -1)
          this.intervalEvents.splice(idx,1);
      },

      // `extra` is merged into the request body as-is (a seating sends { event: 'seated', operatoryNum }).
      // The backend only reads the fields it knows, so extra fields are ignored until it starts using them.
      async _updateODConfirmStatus(aptNum, confirmedStatus, extra = {}) {
        let payload = {
          siteID: this.siteID,
          aptNum: aptNum,
          confirmedStatus: confirmedStatus,
          //...(confirmedStatus === this.practicePulseSettings.inTreatmentRoomStatus.DefNum && { dateTimeSeated: this.formatDateTimeWitTz(moment(), this.allSettings.GENERAL_TIMEZONE.PropertyValue) }),
          //...(confirmedStatus === this.practicePulseSettings.dismissedStatus.DefNum && { dateTimeDismissed: this.formatDateTimeWitTz(moment(), this.allSettings.GENERAL_TIMEZONE.PropertyValue) }),
          ...(confirmedStatus === this.practicePulseSettings.inTreatmentRoomStatus.DefNum && { dateTimeSeated: moment().format('YYYY-MM-DD HH:mm:ss') }),
          ...(confirmedStatus === this.practicePulseSettings.dismissedStatus.DefNum && { dateTimeDismissed: moment().format('YYYY-MM-DD HH:mm:ss') }),
          ...extra,
        }
        //console.log('updateODConfirmStatus payload', payload);
        await this.updateODConfirmStatus(payload);
        //console.log(`in room status updated in OD`);
      }
    },

    computed: {
      
      ...mapState({
        practicePulse: state => state.practicepulse.practicePulse,
        tasksTaggedWithPatients: state => state.tasks.tasksTaggedWithPatients,
      }),      

      ...mapGetters({
        'employeeName' : 'customer/employeeName',  
        'getPatient'    : 'customer/getPatient',
        'getTasksTaggedWithPatient' : 'tasks/getTasksTaggedWithPatient',
      }),

      alertsShortcutsAvailable() {
        if(this.alertsGroupByEmployee && Object.keys(this.alertsGroupByEmployee).length > 0)
          return true;
        else
          return false;
      },

      howManyArrived() {
        if(this.practicePulseSettings && this.pp && this.pp.ops && this.pp.ops.checkin) {
          let arrived = this.pp.ops.checkin.filter(p => p.checkinDefNum === this.practicePulseSettings.arrivedStatus.DefNum);
          return arrived.length;
        } else
          return 0;
      },

      howManyReady() {
        if(this.practicePulseSettings && this.pp && this.pp.ops && this.pp.ops.checkin) {
          let ready = this.pp.ops.checkin.filter(p => p.checkinDefNum === this.practicePulseSettings.readyStatus.DefNum);
          return ready.length;
        } else
          return 0;
      },

      /**
       * How many patients are, RIGHT NOW, over the configured waiting limit.
       *
       * 🔴 REWRITTEN 2026-09-21. This used to be `Object.keys(this.visualAlerts).length`, and
       * `visualAlerts` is a map persisted to Firestore. Two problems with counting it:
       *
       *   1. store/practicepulse.js writes it with setDoc(..., { merge: true }), and Firestore's
       *      merge DEEP-MERGES map fields — it can add and overwrite keys but cannot REMOVE one.
       *      So deleting an entry locally and writing the smaller object left the key on the
       *      server, and the next snapshot brought it straight back. The same action already
       *      handles this for ops.checkin via deleteField(); visualAlerts never got that treatment.
       *      Net effect: the count could only ever grow until the nightly reset.
       *   2. It counted patients who had left check-in entirely.
       *
       * Deriving it from the board's current contents makes both impossible, needs no write, and
       * cannot disagree between two screens in the same office. `visualAlerts` still drives the
       * per-patient red dot, so it stays — it is just no longer the source of truth for the count.
       */
      howManyWaitingForMoreThanX() {
        if(!waitAlertEnabled(this.practicePulseSettings)) return 0;
        if(!this.pp || !this.pp.ops || !this.pp.ops.checkin) return 0;

        const checkin = this.pp.ops.checkin;
        const patients = Array.isArray(checkin) ? checkin : Object.values(checkin);
        const limit = this.practicePulseSettings.elapsedTime;

        return patients.filter(p =>
          p && isOverThreshold(elapsedMs(p.dateTimeReady, this.nowTick), limit)
        ).length;
      },

      howManyCheckedOut() {
        if(this.practicePulseSettings && this.pp && this.pp.ops && this.pp.ops.checkout) {
          if(this.pp.ops.checkout.length > 0) {
            let dismissed = this.pp.ops.checkout.filter(p => p.checkinDefNum === this.practicePulseSettings.dismissedStatus.DefNum);            
            return this.pp.ops.checkout.length - dismissed.length;
          } else
            return 0;
        }
        else
          return 0;        
      },

      howManyDismissed() {
        if(this.practicePulseSettings && this.pp && this.pp.ops && this.pp.ops.checkout) {
          let dismissed = this.pp.ops.checkout.filter(p => p.checkinDefNum === this.practicePulseSettings.dismissedStatus.DefNum);
          return dismissed.length;
        } else
          return 0;
      },

      privacyClass() {  
        let css;      
        if(this.privacyOn) {
          css = `la la-lock px-2 pull-right`;
        } else {
          css = `la la-unlock px-2 pull-right`;
        }
        return css;
      },

      practicePulsePaidFor() {
        if(this.allSettings && this.allSettings.PRACTICE_PULSE_PAID_FOR && this.allSettings.PRACTICE_PULSE_PAID_FOR.PropertyValue === 'TRUE') {
          return true;
        } else
          return false;
      },

      practicePulseSettingsORIG() {
        if(this.allSettings && this.allSettings.PRACTICE_PULSE_SETTINGS && this.allSettings.PRACTICE_PULSE_SETTINGS.PropertyValue !== '') {
          let temp = this.allSettings.PRACTICE_PULSE_SETTINGS.PropertyValue;
          temp = temp.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
          return JSON.parse(temp);
        }
      },

      practicePulseSettings() {
        if(this.practicePulse && this.practicePulse.settings) {
          return this.practicePulse.settings;
        }
      },

      emptyOps() {
        // The "Move to Op" choices: operatories that are ON the board (a column exists in pp.ops)
        // and have no patient in them. This used to return every op in allOps, so occupied rooms
        // were offered, and so were ops not on the board (handleMoveFromCheckin then pushed onto
        // undefined). Shadows the mixin's emptyOps; keyed by OperatoryNum, which the template uses.
        let ops = {};
        if(this.practicePulseSettings && this.practicePulseSettings.allOps && this.pp && this.pp.ops) {
          for(const op of this.practicePulseSettings.allOps) {
            const column = this.pp.ops[op.OperatoryNum];
            if(op.OperatoryNum === 'checkin' || op.OperatoryNum === 'checkout') continue;
            if(Array.isArray(column) && column.length === 0) {
              ops[op.OperatoryNum] = op;
            }
          }
        }
        return ops;
      },

      dragOptions() {
        const options = {
          animation: 150, // Smooth animation for better UX (similar to Kanban)
          group: {
            name: "practice-pulse",
            pull: true,
            put: true
          },
          disabled: false,
          ghostClass: "ghost",
          chosenClass: "chosen", // Visual feedback when item is picked up
          dragClass: "dragging", // Style applied to the dragging element
          forceFallback: false,
          fallbackOnBody: true,
          swapThreshold: 0.65,
          emptyInsertThreshold: 50 // Larger drop zone for easier dropping (was 5)
        };
        return options;
      },

      visualIndicatorStyle() {
        let temp =
        {
          "background-color": this.practicePulseSettings.elapsedColor ? this.practicePulseSettings.elapsedColor : "#D02816",
          "color": "#000000",          
        }

        if(this.practicePulseSettings.elapsedBlink)
          temp = {
            ...temp,            
            "animation": "1s linear infinite",              
          }          
        return temp;
      },

      alertsGroupByEmployee() {
        if(this.practicePulseSettings) {
          return _.groupBy(this.practicePulseSettings.customAlerts, (alert) => alert.employee ? alert.employee.Name  : '');
        }
      },
    },

    watch: {
      practicePulse: {
        immediate: true,
        deep: true,
        handler(newValue, oldValue) {
          // CRITICAL: Don't process Firestore updates during drag operations
          if(window.__isDragging) {
            return;
          }

          if(!_.isEmpty(newValue)) {
            // CRITICAL FIX: Validate incoming data against pending drag operation
            // If we have a pending drag and the incoming data still shows patient in old location,
            // this is stale data that would cause a duplicate - skip it
            if(this.pendingDragOperation && newValue.ops) {
              const { patNum, from } = this.pendingDragOperation;
              const fbOps = newValue.ops;

              // Check if patient is still in the 'from' location in Firebase data
              let patientStillInFrom = false;
              if(from === 'checkin' && fbOps.checkin) {
                // checkin can be object (keyed by patNum) or array
                if(Array.isArray(fbOps.checkin)) {
                  patientStillInFrom = fbOps.checkin.some(p => p.patNum === patNum);
                } else {
                  patientStillInFrom = !!fbOps.checkin[patNum];
                }
              } else if(from && fbOps[from]) {
                patientStillInFrom = Array.isArray(fbOps[from])
                  ? fbOps[from].some(p => p.patNum === patNum)
                  : false;
              }

              // If patient is still in 'from' location, this is stale data - skip it
              // The correct update should have removed the patient from 'from'
              if(patientStillInFrom) {
                console.log('[DRAG] Skipping stale Firebase update - patient still in old location:', from);
                return;
              }

              // Valid update received (patient no longer in 'from') - clear the pending operation
              this.pendingDragOperation = null;
            }

            //console.log(`New value: ${JSON.stringify(newValue)}, Old value: ${JSON.stringify(oldValue)}`);
            //console.log('new value', newValue);
            let temp = {};
            //let _ops = [];            
            let counter = 1;
            let _allPatNums = [];

            let fbOps = newValue.ops;
            //console.log('fbOps', fbOps);

            let fbAllOps = newValue.allOps;
            //console.log('fbAllOps', fbAllOps);
            Vue.set(this.pp, 'allOps', fbAllOps);

            for(const key in fbOps) {
              temp[key] = [];                            
              if(key !== 'checkin') { 
                // if it's checkout then it's an object otherwise it's an array
                if(Array.isArray(fbOps[key])) {                  
                  if(fbOps[key].length > 0) {
                    for(const patient of fbOps[key]) {    
                      //p = this.populateInfo(patient);
                      //console.log(`after populating info for patient`, p);
                      temp[key].push(this.populateInfo(patient));                      
                      _allPatNums.push(patient.patNum);                  
                    }
                    if(key === 'checkout') {                      
                      temp[key] = this.sortCheckout(temp[key]);
                    }
                  }
                } else {
                  for(const patNum in fbOps[key]) { 
                    _allPatNums.push(parseInt(patNum,10));
                    let patObj = fbOps[key][patNum];
                    temp[key].push(this.populateInfo(patObj));
                  }
                }                
              } else {
                // CRITICAL FIX: checkin comes from Firestore as object but needs to be array for template
                // at this point, checkin could be empty array or object with patnum keys
                if(!Array.isArray(fbOps[key])) {
                  // for check in, it's an object with patnum as the key                
                  for(const patNum in fbOps[key]) {                     
                    //Vue.set(this.allPatNums, index, parseInt(patNum,10));
                    _allPatNums.push(parseInt(patNum,10));                    
                    let patObj = fbOps[key][patNum];
                    // let's check the checkin color first to see if we need to enable a timer                  
                    if(this.practicePulseSettings && this.practicePulseSettings.readyStatus && patObj.checkinDefNum === this.practicePulseSettings.readyStatus.DefNum) {
                      // this means that this patient was just marked READY. So let's start a timer for that patient                                      
                      // ⚠️ dateTimeReady CAN be absent on a patient sitting in the ready status —
                      // this file null-checks it twice elsewhere before allowing a move. Calling
                      // .toDate() on it unguarded threw inside this snapshot loop, which meant the
                      // remaining operatory columns were never built and the board half-rendered
                      // with no error anywhere. Skip the timer instead; the patient still shows.
                      if(!this.patientTimers[patNum] && patObj.dateTimeReady && typeof patObj.dateTimeReady.toDate === 'function') {
                        Vue.set(this.patientTimers, patNum, {});
                        readyIntervalId = setInterval(this.updatePatientTime.bind(null, patNum,patObj.dateTimeReady.toDate()),1000);
                        this.intervalEvents.push(readyIntervalId);
                        // now save this intervalid into timer object so we can cancel that specific timer later when the patient is moved from check in to some other op perhaps
                        Vue.set(this.patientTimers[patNum], 'readyIntervalId', readyIntervalId);
                      }
                    }
                    temp[key].push(this.populateInfo(patObj));
                  }
                  // now we want to sort the checkin array only by appt time
                  temp[key] = this.sortCheckin(temp[key]);
                } else {
                  // checkin is already an array, just process it
                  if(fbOps[key].length > 0) {
                    for(const patient of fbOps[key]) {
                      _allPatNums.push(patient.patNum);
                      // Check if patient needs timer
                      if(this.practicePulseSettings && this.practicePulseSettings.readyStatus && patient.checkinDefNum === this.practicePulseSettings.readyStatus.DefNum) {
                        // same guard as the object-shaped branch above — see the note there
                        if(!this.patientTimers[patient.patNum] && patient.dateTimeReady && typeof patient.dateTimeReady.toDate === 'function') {
                          Vue.set(this.patientTimers, patient.patNum, {});
                          readyIntervalId = setInterval(this.updatePatientTime.bind(null, patient.patNum, patient.dateTimeReady.toDate()), 1000);
                          this.intervalEvents.push(readyIntervalId);
                          Vue.set(this.patientTimers[patient.patNum], 'readyIntervalId', readyIntervalId);
                        }
                      }
                      temp[key].push(this.populateInfo(patient));
                    }
                    temp[key] = this.sortCheckin(temp[key]);
                  }
                }                
              }
              counter++;     
            }

            Vue.set(this, 'allPatNums', [...new Set(_allPatNums)]);
                        
            //console.log('this.pp.ops before doing vue.set', this.pp.ops);
            //console.log('temp', temp);

            Vue.set(this.pp, 'ops', temp);
            Vue.set(this.pp, 'alerts', newValue.alerts);     
                        
            Vue.set(this, 'alertTimers', []);
            //Vue.delete(this, 'alertTimers');

            this.alertIntervalEvents.map(intervalEvent => {
              clearInterval(intervalEvent)
            });

            //console.log('this.pp.ops after doing vue.set', this.pp.ops);
            //console.log('temp', temp);

            if(newValue.visualAlerts)
              Vue.set(this, 'visualAlerts', newValue.visualAlerts);

            //console.log('about to set alert timers');
            this.setAlertTimers();
            //this.setPatientTimers();
            this.ppLoaded = true;
          }
        }        
      },

      'practicePulse.alerts': {
        handler (newValue, oldValue) {
          let _this = this;
          //console.log('alerts changed', newValue, oldValue);          
          if(oldValue) {
            /*
            let diff = _.differenceWith(newValue, oldValue, function(arrValue, othValue) {
              return arrValue.op === othValue.op 
                    && arrValue.pingFromEmployeeID === othValue.pingFromEmployeeID
                    && arrValue.pingToEmployeeID === othValue.pingToEmployeeID
                    && arrValue.msg === othValue.msg
            });
            */

            let diff = _.differenceWith(newValue, oldValue, _.isEqual);
            //console.log('diff', diff);
            //console.log('diff2', diff2);

            if(diff.length > 0) {            
              //console.log('diff in practicePulse.alerts', diff);              
              let alertSound;
              for(const alert of diff) {
                // no entry for this provider (or no providerSettings at all) = no sound, not a throw
                let providerSetting = _this.practicePulseSettings && _this.practicePulseSettings.providerSettings
                  ? _this.practicePulseSettings.providerSettings[alert.pingToEmployeeID] : null;
                let sound = providerSetting ? providerSetting.sound : null;
                //console.log('found sound', sound);
                if(sound) {
                  alertSound = sound.url;         
                  console.log('url', sound.url);  
                  if(!localStorage.getItem(`${sound.name}_base64`)) {
                    console.log('sound not already downloaded. trying to download now');
                    _this.downloadSound(sound);                    
                    console.log(`downloaded sound file ${sound.url}. now trying to play sound`, sound.url);
                  }
                  _this.playSound(sound);
                }
                //Push.create("Practice Pulse Alert");
                let pingTo = this.employeeName(alert.pingToEmployeeID);
                if(pingTo) {
                  let title = `${this.employeeName(alert.pingToEmployeeID)} needed in ${alert.opName}`;
                  let body = alert.msg;
                  this.createAlertPushNotification(title, body);
                }
              }              
            } else {
              //console.log("there was no diff. so not playing any sound");
            }
          }
        },        
      },

      // Firestore listener lifecycle: this watcher fires on EVERY check-in / check-out
      // change, so it re-subscribes constantly. Before 2026-09-02 it dispatched
      // fire-and-forget and never unsubscribed, so listeners accumulated for the whole
      // session, outlived the page, and a stale one could overwrite the shared slot
      // `state.tasks.tasksTaggedWithPatients` with an older patient set.
      // Vue ignores a watcher's return value, so making this async is safe.
      async allPatNums(newValue, oldValue) {
        //console.log('allPatNums changed', newValue, oldValue);

        // Drop the previous listener FIRST — unconditionally, including when the guard
        // below is false (board emptied, or showTasks turned off), so an old patient set
        // can never keep writing the shared slot.
        this.teardownPatientTasksListener();
        // Invalidate any in-flight subscribe BEFORE the await so a slow resolution knows
        // it has been superseded (PatientTasksCard.vue::subscribe() uses the same guard).
        const token = ++this.patientTasksSubscribeToken;

        /*
          Important note: getTasksTaggedWithPatients uses firebase's array-contains-any function which only allows to check for 10 values
          which means I'm going to have to reduce the patientsToLookFor array to only have at most 10 items. If it has 12 for example, I'm going to reduce it to 10
          Mostly this won't create an issue because I don't imagine any office having more than 10 patients checked in at a time (thought it could happen which is why
          i'm building this fix)

          So what this means is if there's ever more than 10 people checked-in, then I won't know if 11th+ people have tasks or not. I'll only check for first 10
          but that's ok. I believe this is a rare case
        */

        if(newValue.length > 0 && this.practicePulseSettings && this.practicePulseSettings.showTasks === 'TRUE') {
          let temp;
          if(newValue.length > 10) {
            temp = [...newValue];
            temp.splice(10);
          }
          else
            temp = newValue;

          let payload = {
            siteID: this.siteID,
            patientsToLookFor: temp
          }
          const unsub = await this.getTasksTaggedWithPatients(payload);
          // A newer allPatNums change started another subscribe while we awaited — this
          // resolution is stale. Never store it and never leave its listener live, or the
          // older patient set would keep overwriting the shared slot.
          if (token !== this.patientTasksSubscribeToken) {
            if (typeof unsub === 'function') {
              unsub();
              this.$store.commit('customer/removeFirestoreListener', unsub);
            }
            return;
          }
          this.patientTasksUnsubscribe = unsub;
        }
      },

      tasksTaggedWithPatients(newValue, oldValue) {   
        //console.log('watch change tasks tagged with patients', newValue, oldValue);
        let _this = this;
        _this.taggedTasks = {};        
        for(const task of newValue) {
          if(task.taggedPatients && task.taggedPatients.length > 0) {
            let taggedPatients = task.taggedPatients;            
            for(const patNum of taggedPatients) {              
              if(Array.isArray(_this.taggedTasks[patNum]))
                _this.taggedTasks[patNum].push(task);
              else {
                _this.taggedTasks[patNum] = [];
                _this.taggedTasks[patNum].push(task);
              }
            }
          }
        }
      },

      privacyOn(newValue, oldValue) {
        //console.log('privacy change', newValue, oldValue);
        //console.log('this.pp.ops', this.pp.ops);
        let p;
        for(const operatory in this.pp.ops) {
          let op = this.pp.ops[operatory];
          //console.log('op', op);
          for(const patient of op) {
            p = this.getPatient(patient.patNum);
            if(p) {
              if(newValue)
                patient.name = `${p.FName}`;
              else
                patient.name = `${p.FName} ${p.LName}`;
            }
          }
        }
      }
    }
  };
  </script>

  <style>
    :root {
      --visual-indicator-color: rgb(208,40,22);
    }

    @keyframes blink {
      0% {
          background-color: var(--visual-indicator-color);
          /* border: 3px solid #666; */
        }
        100% {
          background-color: var(--visual-indicator-color);
          /* border: 3px solid #666; */
        }
    }
  </style>

  <style scoped>
    /*
    .col-md-3 {
      width:350px;height:200px;
      border: solid 1px gray;
      background-color: #ffffff;
      box-shadow: 3px -2px 5px  rgba(0,0,0,0.6);
      -moz-box-shadow: 3px -2px 5px  rgba(0,0,0,0.6);
      -webkit-box-shadow: 3px -2px 5px  rgba(0,0,0,0.6);
      -o-box-shadow: 3px -2px 5px  rgba(0,0,0,0.6);
      border-radius:25px;
      margin-left: 15px;
    }
    */

    
    /*.col-md-4 {
      margin-bottom: 10px;
      padding-left: 5px;
      padding-right: 5px;
    }*/

    .widget {
      margin-bottom: 20px;
      /*min-height: 100px;*/
    }


    .ppalert hr {
      margin-top: 0px;
    }

    .ppalert .config {
      position: relative !important;
      right: -20px !important;
      cursor: pointer;
    }

    .flip-list-move {
      transition: transform 0.5s;
    }

    .no-move {
      transition: transform 0s;
    }

    .ghost {
      opacity: 0.5;
      background: #c8ebfb;
      border: 2px dashed #007bff;
      border-radius: 4px;
    }

    /* Visual feedback when item is picked up */
    .chosen {
      opacity: 0.9;
      box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
      transform: scale(1.02);
    }

    /* Style for the element being dragged */
    .dragging {
      opacity: 0.8;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      transform: rotate(2deg);
    }

    .list-group {
      min-height: 50px;
      padding: 5px;
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }

    /* Highlight drop zone when hovering */
    .list-group:hover {
      background-color: rgba(0, 123, 255, 0.03);
    }

    .list-group-item {
      cursor: move;
    }

    .list-group-item i {
      cursor: pointer;
    }

    .chat-section-wrapper {
      margin-top: 30px;
      padding: 20px;
      border-top: 1px solid #e0e0e0;
      background: #f9fbfd;
      border-radius: 8px;
      overflow: visible;
    }

    .dismissed {
      opacity: 0.3;
    }

    .noalert {
      background-color: gray;
      color: white;
      font-weight: bold;
      text-align: center;
    }

  </style>

  <!-- Unscoped: multiselect dropdown must render above other elements -->
  <style>
    .chat-section-wrapper {
      overflow: visible !important;
    }
  </style>

  <style src="./ppstyles.scss" lang="scss" />